// components/common/PackingListPreview/PackingListPreview.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { getDoc, doc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import styles from './PackingListPreview.module.css';

// Icons
import {
    FiX,
    FiRefreshCw,
    FiFileText,
    FiPrinter,
    FiAlertCircle
} from 'react-icons/fi';

// Constants
const MAX_HEIGHT_AIR = 160;
const MAX_HEIGHT_SEA = 220;

// ============================================
// 1. HELPER FUNCTIONS
// ============================================

const formatNumber = (value, decimals = 3) => {
    if (!value && value !== 0) return '0';
    return Number(value).toFixed(decimals);
};

const formatWeight = (value) => {
    if (!value && value !== 0) return '0,000';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const formatVolume = (value) => {
    if (!value && value !== 0) return '0,000';
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
};

const formatCurrentDateTime = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const isWaterMaterial = (material) => {
    if (!material) return false;
    const name = (material.name || material.inciName || material.commercialName || '').toUpperCase();
    return name.includes('ÁGUA') || name.includes('AGUA') || name === 'WATER';
};

const groupPalletsByDimension = (physicalPallets = []) => {
    const groups = {};
    physicalPallets.forEach((pallet) => {
        const roundedHeight = Math.round(pallet.finalHeight || 0);
        const dimKey = `${pallet.width || 100} x ${pallet.depth || 120} x ${roundedHeight} cm`;
        if (!groups[dimKey]) {
            groups[dimKey] = {
                count: 0,
                dimensions: dimKey,
                isPartial: pallet.isPartial || false,
                width: pallet.width || 100,
                depth: pallet.depth || 120,
                height: roundedHeight
            };
        }
        groups[dimKey].count += 1;
        if (pallet.isPartial) {
            groups[dimKey].isPartial = true;
        }
    });
    return Object.values(groups);
};

/**
 * Fetches LIVE client data from Firestore
 * This ALWAYS gets the most up-to-date information
 */
const fetchLiveClientData = async (clientId) => {
    if (!clientId) {
        console.warn('⚠️ No clientId provided to fetchLiveClientData');
        return null;
    }

    try {
        console.log('🔍 Fetching LIVE client data for ID:', clientId);

        const clientRef = doc(db, 'clients', clientId);
        const clientSnap = await getDoc(clientRef);

        if (!clientSnap.exists()) {
            console.warn('⚠️ Client document not found for ID:', clientId);
            return null;
        }

        const clientData = clientSnap.data();
        console.log('✅ Live client data fetched successfully:', {
            id: clientId,
            razaoSocial: clientData.razaoSocial,
            nomeFantasia: clientData.nomeFantasia,
            addressesCount: clientData.addresses?.length || 0
        });

        return clientData;
    } catch (error) {
        console.error('❌ Error fetching live client data:', error);
        return null;
    }
};

/**
 * Extracts contact info from a client's addresses array
 */
const extractContactsFromAddresses = (addresses, isImporter = false) => {
    const contacts = { email: '', phone: '', name: '' };

    if (!addresses || !Array.isArray(addresses)) return contacts;

    // Filter addresses based on isImporter flag
    const filteredAddresses = addresses.filter(addr => addr.isImporter === isImporter);

    for (const addr of filteredAddresses) {
        if (addr.contacts && Array.isArray(addr.contacts) && addr.contacts.length > 0) {
            for (const contact of addr.contacts) {
                if (!contacts.email && contact.email) {
                    contacts.email = contact.email;
                }
                if (!contacts.phone && contact.telefone) {
                    contacts.phone = contact.telefone;
                }
                if (!contacts.name && contact.nome) {
                    contacts.name = contact.nome;
                }
            }
            // If we found all three, break early
            if (contacts.email && contacts.phone && contacts.name) break;
        }
    }

    return contacts;
};

/**
 * Gets the formatted delivery address from client data
 */
const getDeliveryAddressFromClient = (clientData) => {
    if (!clientData || !clientData.addresses) return '';

    const regularAddresses = clientData.addresses.filter(addr => addr.isImporter !== true);
    if (regularAddresses.length === 0) return '';

    const primaryAddr = regularAddresses[0];
    const addrStr = [
        primaryAddr.logradouro || '',
        primaryAddr.numero || '',
        primaryAddr.complemento || '',
        primaryAddr.bairro || '',
        primaryAddr.cidade || '',
        primaryAddr.uf || '',
        primaryAddr.cep || ''
    ].filter(Boolean).join(' ').trim();

    return addrStr || '';
};

// ============================================
// 2. DATA FETCHING FUNCTIONS - IMPROVED
// ============================================

/**
 * Fetch all pallet types with their box configurations
 */
const fetchAllPalletTypes = async () => {
    try {
        const palletRef = collection(db, 'parameters', 'types', 'tipo-palete');
        const snapshot = await getDocs(palletRef);
        const pallets = [];
        snapshot.forEach(docSnap => {
            pallets.push({ id: docSnap.id, ...docSnap.data() });
        });

        console.log(`📦 ${pallets.length} pallet types loaded`);
        return pallets;
    } catch (error) {
        console.error('Erro ao buscar tipos de palete:', error);
        return [];
    }
};

/**
 * Fetch all box types with their parameters
 */
const fetchAllBoxTypes = async () => {
    try {
        const boxRef = collection(db, 'parameters', 'types', 'tipo-caixa');
        const snapshot = await getDocs(boxRef);
        const boxes = [];
        snapshot.forEach(docSnap => {
            boxes.push({ id: docSnap.id, ...docSnap.data() });
        });

        console.log(`📦 ${boxes.length} box types loaded`);
        return boxes;
    } catch (error) {
        console.error('Erro ao buscar tipos de caixa:', error);
        return [];
    }
};

/**
 * Fetch complete product details including box type and NCM
 * ALWAYS fetches fresh data from Firestore
 */
const fetchProductDetailsForPacking = async (productId) => {
    if (!productId) return null;

    try {
        console.log(`🔍 Fetching product details for: ${productId}`);

        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            console.warn(`⚠️ Product ${productId} not found`);
            return null;
        }

        const data = productSnap.data();
        const technical = data.technical || {};

        // --- 1. Get Box Type ID (priority order) ---
        let boxTypeId = data.boxTypeId ||
            technical.boxTypeId ||
            data.fiscal?.boxTypeId ||
            null;

        // --- 2. Get Units Per Box - IMPROVED PRIORITY ---
        let unitsPerBox = 0;
        let boxTypeConfigs = [];

        // PRIORITY 1: Check technical.boxTypeConfigs first
        if (technical.boxTypeConfigs && Array.isArray(technical.boxTypeConfigs) && technical.boxTypeConfigs.length > 0) {
            boxTypeConfigs = technical.boxTypeConfigs;
            const firstConfig = technical.boxTypeConfigs[0];
            if (firstConfig && firstConfig.unitsPerBox) {
                unitsPerBox = parseInt(firstConfig.unitsPerBox) || 0;
                console.log(`✅ Units per box from technical.boxTypeConfigs: ${unitsPerBox}`);
            }
            if (!boxTypeId && firstConfig) {
                boxTypeId = firstConfig.boxTypeId;
            }
        }

        // PRIORITY 2: Check root data.unitsPerBox
        if (unitsPerBox === 0 && data.unitsPerBox !== undefined && data.unitsPerBox !== null) {
            unitsPerBox = parseInt(data.unitsPerBox) || 0;
            console.log(`✅ Units per box from data.unitsPerBox: ${unitsPerBox}`);
        }

        // PRIORITY 3: Check technical.unitsPerBox (direct field)
        if (unitsPerBox === 0 && technical.unitsPerBox !== undefined && technical.unitsPerBox !== null) {
            unitsPerBox = parseInt(technical.unitsPerBox) || 0;
            console.log(`✅ Units per box from technical.unitsPerBox: ${unitsPerBox}`);
        }

        // PRIORITY 4: Check fiscal.unitsPerBox
        if (unitsPerBox === 0 && data.fiscal?.unitsPerBox !== undefined && data.fiscal?.unitsPerBox !== null) {
            unitsPerBox = parseInt(data.fiscal.unitsPerBox) || 0;
            console.log(`✅ Units per box from fiscal.unitsPerBox: ${unitsPerBox}`);
        }

        // --- 3. Fetch Box Type details from parameters ---
        let boxTypeData = null;
        if (boxTypeId) {
            try {
                const boxTypeRef = doc(db, 'parameters', 'types', 'tipo-caixa', boxTypeId);
                const boxTypeSnap = await getDoc(boxTypeRef);
                if (boxTypeSnap.exists()) {
                    boxTypeData = boxTypeSnap.data();

                    // PRIORITY 5: Fallback to box type's default units per box
                    if (unitsPerBox === 0 && boxTypeData.unitsPerBox) {
                        unitsPerBox = parseInt(boxTypeData.unitsPerBox) || 0;
                        console.log(`✅ Units per box from box type: ${unitsPerBox}`);
                    }

                    console.log(`📦 Box type found: ${boxTypeData.name} (${boxTypeId})`);
                } else {
                    console.warn(`⚠️ Box type ${boxTypeId} not found in parameters`);
                }
            } catch (e) {
                console.warn('Erro ao buscar tipo de caixa:', e);
            }
        }

        // --- 4. Get weights ---
        const gramaturaUnit = technical.gramaturaUnit || data.gramaturaUnit || 'g';
        const unitLower = String(gramaturaUnit).toLowerCase();

        let netWeightInKg = 0;
        if (technical.gramatura !== undefined && technical.gramatura !== null && parseFloat(technical.gramatura) > 0) {
            const rawGram = parseFloat(technical.gramatura) || 0;
            netWeightInKg = (unitLower === 'g' || unitLower === 'ml') ? rawGram / 1000 : rawGram;
        } else if (data.netWeight !== undefined && data.netWeight !== null) {
            netWeightInKg = parseFloat(data.netWeight) || 0;
        }

        let grossWeightInKg = 0;
        if (technical.grossWeight !== undefined && technical.grossWeight !== null && parseFloat(technical.grossWeight) > 0) {
            const rawGross = parseFloat(technical.grossWeight) || 0;
            grossWeightInKg = (unitLower === 'g' || unitLower === 'ml') ? rawGross / 1000 : rawGross;
        } else if (data.grossWeight !== undefined && data.grossWeight !== null && parseFloat(data.grossWeight) > 0) {
            grossWeightInKg = parseFloat(data.grossWeight) || 0;
        }

        // --- 5. Get box dimensions ---
        let boxHeight = 0, boxWidth = 0, boxLength = 0;
        if (technical.boxHeight !== undefined && technical.boxHeight !== null) boxHeight = parseFloat(technical.boxHeight) || 0;
        if (technical.boxWidth !== undefined && technical.boxWidth !== null) boxWidth = parseFloat(technical.boxWidth) || 0;
        if (technical.boxLength !== undefined && technical.boxLength !== null) boxLength = parseFloat(technical.boxLength) || 0;
        if (boxHeight === 0 && data.boxHeight !== undefined && data.boxHeight !== null) boxHeight = parseFloat(data.boxHeight) || 0;
        if (boxWidth === 0 && data.boxWidth !== undefined && data.boxWidth !== null) boxWidth = parseFloat(data.boxWidth) || 0;
        if (boxLength === 0 && data.boxLength !== undefined && data.boxLength !== null) boxLength = parseFloat(data.boxLength) || 0;

        // Fallback to box type dimensions
        if (boxTypeData) {
            if (boxHeight === 0 && boxTypeData.height) boxHeight = parseFloat(boxTypeData.height) || 0;
            if (boxWidth === 0 && boxTypeData.width) boxWidth = parseFloat(boxTypeData.width) || 0;
            if (boxLength === 0 && boxTypeData.length) boxLength = parseFloat(boxTypeData.length) || 0;
        }

        // --- 6. Get NCM ---
        let ncmCode = '';
        if (data.fiscal?.ncmId) {
            try {
                const ncmRef = doc(db, 'parameters/data/ncm', data.fiscal.ncmId);
                const ncmSnap = await getDoc(ncmRef);
                if (ncmSnap.exists()) {
                    ncmCode = ncmSnap.data().ncmCode || '';
                }
            } catch (e) {
                console.warn('Erro ao buscar NCM:', e);
            }
        }

        // --- LOG FINAL RESULT ---
        console.log(`✅ Product ${data.code}: unitsPerBox=${unitsPerBox}, boxTypeId=${boxTypeId}`);

        const result = {
            id: productId,
            unitsPerBox,
            boxTypeConfigs,
            boxTypeId,
            boxTypeData,
            gramaturaUnit,
            netWeightInKg,
            grossWeightInKg,
            boxHeight,
            boxWidth,
            boxLength,
            code: data.code || '',
            commercialName: data.commercialName || '',
            name: data.name || '',
            ncm: ncmCode,
            technical
        };

        return result;

    } catch (error) {
        console.error('Erro ao buscar produto:', error);
        return null;
    }
};

/**
 * Fetch stock info for a product (lot, expiration date, etc.)
 */
const fetchStockInfoForProduct = async (productId) => {
    try {
        if (!productId) return null;

        const stockRef = collection(db, 'current-stock');
        const q = query(stockRef, where('rawMaterialId', '==', productId), where('quantity', '>', 0.001));
        const snapshot = await getDocs(q);

        if (snapshot.empty) return null;

        let stockItems = [];
        snapshot.forEach(docSnap => {
            stockItems.push({ id: docSnap.id, ...docSnap.data() });
        });

        // Sort by expiration date (FIFO)
        stockItems.sort((a, b) => {
            const dateA = a.expirationDate?.toDate?.() || new Date(a.expirationDate || 0);
            const dateB = b.expirationDate?.toDate?.() || new Date(b.expirationDate || 0);
            return dateA - dateB;
        });

        const selectedStock = stockItems[0];
        if (!selectedStock) return null;

        const formatDateMMYYYY = (dateValue) => {
            if (!dateValue) return '';
            try {
                let date;
                if (dateValue.toDate) date = dateValue.toDate();
                else if (typeof dateValue === 'string') date = new Date(dateValue);
                else if (dateValue instanceof Date) date = dateValue;
                else return '';
                if (isNaN(date.getTime())) return '';
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${month}/${year}`;
            } catch { return ''; }
        };

        return {
            lotNumber: selectedStock.lotNumber || '',
            manufacturerDateFormatted: formatDateMMYYYY(selectedStock.manufacturerDate || selectedStock.manufactureDate),
            expirationDateFormatted: formatDateMMYYYY(selectedStock.expirationDate),
            quantityAvailable: selectedStock.quantity || 0,
            location: selectedStock.locationDescription || ''
        };
    } catch (error) {
        console.error('Erro ao buscar informações de estoque:', error);
        return null;
    }
};

/**
 * Determine shipping method from order
 */
const determineShippingMethod = (order) => {
    if (order.shippingMethod) {
        const method = order.shippingMethod.toLowerCase();
        if (method === 'air' || method === 'aereo' || method === 'airfreight') return 'air';
        if (method === 'sea' || method === 'maritimo' || method === 'maritime') return 'sea';
    }
    if (order.transportMode) {
        const mode = order.transportMode.toLowerCase();
        if (mode === 'air' || mode === 'aereo') return 'air';
        if (mode === 'sea' || mode === 'maritimo') return 'sea';
    }
    if (order.shippingType) {
        const type = order.shippingType.toLowerCase();
        if (type.includes('air') || type.includes('aereo')) return 'air';
        if (type.includes('sea') || type.includes('maritimo')) return 'sea';
    }
    return 'sea';
};

/**
 * Get pallet configuration for a specific box type
 * IMPROVED: Now checks multiple sources and logs warnings
 */
const getPalletForBoxType = (boxTypeId, allPalletTypes, selectedPalletTypeId = null) => {
    if (!boxTypeId || !allPalletTypes || allPalletTypes.length === 0) {
        console.warn(`⚠️ No pallet types available or boxTypeId missing: ${boxTypeId}`);
        return null;
    }

    // Try selected pallet type first
    if (selectedPalletTypeId) {
        const selectedPallet = allPalletTypes.find(p => p.id === selectedPalletTypeId);
        if (selectedPallet) {
            const config = selectedPallet.boxConfigs?.find(c => c.boxTypeId === boxTypeId);
            if (config) {
                console.log(`✅ Found config for box ${boxTypeId} in selected pallet ${selectedPallet.name}`);
                return { pallet: selectedPallet, config };
            }
            console.warn(`⚠️ Box type ${boxTypeId} not configured in selected pallet ${selectedPallet.name}`);
        }
    }

    // Try any pallet type
    for (const pallet of allPalletTypes) {
        const config = pallet.boxConfigs?.find(c => c.boxTypeId === boxTypeId);
        if (config) {
            console.log(`✅ Found config for box ${boxTypeId} in pallet ${pallet.name}`);
            return { pallet, config };
        }
    }

    console.warn(`⚠️ No pallet configuration found for box type ${boxTypeId}`);
    return null;
};

/**
 * Calculate pallet metrics based on box type and shipping method
 * IMPROVED: No hardcoded fallback values
 */
const calculatePalletMetrics = (pallet, boxConfig, boxDimensions, shippingMethod, actualBoxesOnPallet) => {
    const isAir = shippingMethod === 'air';
    const maxHeight = isAir ? MAX_HEIGHT_AIR : MAX_HEIGHT_SEA;

    // Get dimensions from pallet
    const width = parseFloat(pallet?.palletWidth) || 100;
    const depth = parseFloat(pallet?.palletDepth) || 120;
    const baseHeight = parseFloat(pallet?.palletBaseHeight) || parseFloat(pallet?.palletHeight) || 15;
    const palletWeight = parseFloat(pallet?.palletWeight) || 20;

    // Get boxes per pallet from configuration
    const boxesPerPallet = isAir
        ? parseFloat(boxConfig?.boxesPerPalletAir) || 0
        : parseFloat(boxConfig?.boxesPerPalletSea) || 0;

    const boxesPerLayer = isAir
        ? parseFloat(boxConfig?.boxesPerLayerAir) || 0
        : parseFloat(boxConfig?.boxesPerLayerSea) || 0;

    const boxHeight = parseFloat(boxDimensions?.height) || 0;

    // Use actual boxes if provided, otherwise use configured boxes per pallet
    const effectiveBoxes = actualBoxesOnPallet !== undefined && actualBoxesOnPallet > 0
        ? actualBoxesOnPallet
        : boxesPerPallet;

    let totalHeight = 0;
    let layers = 0;

    if (boxHeight > 0 && boxesPerLayer > 0 && effectiveBoxes > 0) {
        layers = Math.ceil(effectiveBoxes / boxesPerLayer);
        totalHeight = baseHeight + (boxHeight * layers);
    } else if (pallet?.palletHeight && !pallet?.palletBaseHeight) {
        totalHeight = parseFloat(pallet.palletHeight);
        layers = 1;
    } else {
        totalHeight = baseHeight + (boxHeight || 30);
        layers = 1;
    }

    // Cap at max height
    if (totalHeight > maxHeight) totalHeight = maxHeight;

    const volume = (width * depth * totalHeight) / 1000000;

    return {
        width, depth, totalHeight,
        dimensionsStr: `${width} x ${depth} x ${Math.round(totalHeight)}`,
        volume, layers, boxesPerPallet, boxesPerLayer, baseHeight, palletWeight,
        effectiveBoxHeight: boxHeight,
        effectiveLayers: layers,
        effectiveBoxes: effectiveBoxes
    };
};

// ============================================
// 3. MAIN CALCULATION FUNCTION - IMPROVED
// ============================================

const calculatePackingData = async (order, selectedPalletTypeId = null) => {
    if (!order || !order.items || order.items.length === 0) {
        console.warn('⚠️ Order has no items');
        return null;
    }

    try {
        console.log(`📦 Calculating packing data for order ${order.code}`);
        console.log(`📦 Shipping method: ${determineShippingMethod(order)}`);

        const allPalletTypes = await fetchAllPalletTypes();
        const allBoxTypes = await fetchAllBoxTypes();
        const shippingMethod = determineShippingMethod(order);
        const effectivePalletTypeId = selectedPalletTypeId !== null ? selectedPalletTypeId : (order.palletTypeId || null);

        const boxTypeMap = new Map();
        allBoxTypes.forEach(box => boxTypeMap.set(box.id, box));

        // ============================================================
        // FETCH LIVE CLIENT DATA FOR PACKING LIST
        // ============================================================
        let liveClientData = null;
        let clientContacts = { email: '', phone: '', name: '' };
        let deliveryAddress = order.deliveryAddress || order.destination || '';
        let clientName = order.clientName || '';

        if (order.clientId) {
            liveClientData = await fetchLiveClientData(order.clientId);
            if (liveClientData) {
                clientName = liveClientData.razaoSocial ||
                    liveClientData.nomeFantasia ||
                    liveClientData.nome ||
                    order.clientName ||
                    '';

                const contactsFromLive = extractContactsFromAddresses(liveClientData.addresses, false);
                clientContacts = {
                    email: contactsFromLive.email || order.userEmail || order.contactEmail || '',
                    phone: contactsFromLive.phone || order.contactPhone || '',
                    name: contactsFromLive.name || clientName
                };

                const addrFromLive = getDeliveryAddressFromClient(liveClientData);
                if (addrFromLive) deliveryAddress = addrFromLive;
            }
        }

        // Fallback to order data
        if (!clientName) clientName = order.clientName || '';
        if (!clientContacts.email) clientContacts.email = order.userEmail || order.contactEmail || '';
        if (!clientContacts.phone) clientContacts.phone = order.contactPhone || '';

        // ============================================================
        // CALCULATE PER ITEM
        // ============================================================

        let totalCustomerUnits = 0, totalBoxes = 0, totalNetWeight = 0, totalGrossWeight = 0, totalPalletsFloat = 0;
        const itemDetails = [];
        let firstPalletWidth = 0, firstPalletDepth = 0, firstPalletBaseHeight = 0;
        let firstBoxHeight = 0, firstBoxWidth = 0, firstBoxLength = 0;
        let firstBoxesPerPallet = 0, firstBoxesPerLayer = 0;
        let firstBoxDimensions = { height: 0, width: 0, length: 0 };
        const maxHeight = shippingMethod === 'air' ? MAX_HEIGHT_AIR : MAX_HEIGHT_SEA;

        // Store pallet configuration for each product
        const productPalletConfigs = [];

        for (const item of order.items) {
            const productData = await fetchProductDetailsForPacking(item.productId);
            const stockInfo = await fetchStockInfoForProduct(item.productId);
            const quantity = parseFloat(item.quantity) || 0;

            let unitsPerBox = 0, boxTypeId = null, boxTypeName = '';
            let boxDimensions = { height: 0, width: 0, length: 0 };
            let boxTypeData = null;

            if (productData) {
                unitsPerBox = productData.unitsPerBox || 0;
                boxTypeId = productData.boxTypeId || null;
                boxDimensions = {
                    height: productData.boxHeight || 0,
                    width: productData.boxWidth || 0,
                    length: productData.boxLength || 0
                };
                boxTypeData = productData.boxTypeData;
            }

            // Get box type name
            if (boxTypeId && boxTypeMap.has(boxTypeId)) {
                const boxType = boxTypeMap.get(boxTypeId);
                boxTypeName = boxType.name || '';
                if (boxDimensions.height === 0) boxDimensions.height = boxType.height || 0;
                if (boxDimensions.width === 0) boxDimensions.width = boxType.width || 0;
                if (boxDimensions.length === 0) boxDimensions.length = boxType.length || 0;
            }

            // Store first box dimensions
            if (firstBoxHeight === 0 && boxDimensions.height > 0) {
                firstBoxHeight = boxDimensions.height;
                firstBoxWidth = boxDimensions.width;
                firstBoxLength = boxDimensions.length;
                firstBoxDimensions = { ...boxDimensions };
            }

            // ============================================================
            // FIND PALLET CONFIGURATION FOR THIS BOX TYPE
            // ============================================================
            let matchingPallet = null, boxConfig = null;
            let boxesPerPallet = 0;
            let boxesPerLayer = 0;

            if (boxTypeId && allPalletTypes.length > 0) {
                const result = getPalletForBoxType(boxTypeId, allPalletTypes, effectivePalletTypeId);
                if (result) {
                    matchingPallet = result.pallet;
                    boxConfig = result.config;

                    // Get boxes per pallet based on shipping method
                    const isAir = shippingMethod === 'air';
                    boxesPerPallet = isAir
                        ? parseFloat(boxConfig?.boxesPerPalletAir) || 0
                        : parseFloat(boxConfig?.boxesPerPalletSea) || 0;
                    boxesPerLayer = isAir
                        ? parseFloat(boxConfig?.boxesPerLayerAir) || 0
                        : parseFloat(boxConfig?.boxesPerLayerSea) || 0;

                    // Store first pallet dimensions
                    if (firstPalletWidth === 0) {
                        firstPalletWidth = parseFloat(matchingPallet.palletWidth) || 100;
                        firstPalletDepth = parseFloat(matchingPallet.palletDepth) || 120;
                        firstPalletBaseHeight = parseFloat(matchingPallet.palletBaseHeight) || parseFloat(matchingPallet.palletHeight) || 15;
                    }

                    console.log(`📦 Product ${item.code}: boxesPerPallet=${boxesPerPallet}, boxesPerLayer=${boxesPerLayer}`);
                } else {
                    console.warn(`⚠️ No pallet config for box type ${boxTypeId} (product ${item.code})`);
                }
            }

            // ============================================================
            // CRITICAL: NO HARDCODED FALLBACK - Use box type configuration
            // ============================================================
            // If boxesPerPallet is still 0, try to derive from box type
            if (boxesPerPallet === 0 && boxTypeData) {
                // Try to get from box type's default pallet configuration
                if (boxTypeData.defaultPalletConfig) {
                    const isAir = shippingMethod === 'air';
                    boxesPerPallet = isAir
                        ? parseFloat(boxTypeData.defaultPalletConfig.boxesPerPalletAir) || 0
                        : parseFloat(boxTypeData.defaultPalletConfig.boxesPerPalletSea) || 0;
                    boxesPerLayer = isAir
                        ? parseFloat(boxTypeData.defaultPalletConfig.boxesPerLayerAir) || 0
                        : parseFloat(boxTypeData.defaultPalletConfig.boxesPerLayerSea) || 0;

                    if (boxesPerPallet > 0) {
                        console.log(`📦 Using default pallet config from box type: ${boxesPerPallet} boxes/pallet`);
                    }
                }

                // If still 0, try to calculate from dimensions
                if (boxesPerPallet === 0 && boxTypeData.width && boxTypeData.length) {
                    const boxArea = (parseFloat(boxTypeData.width) / 100) * (parseFloat(boxTypeData.length) / 100);
                    const palletArea = 1.2 * 1.0; // Standard pallet area (120cm x 100cm)
                    const maxBoxes = Math.floor(palletArea / boxArea);
                    // Estimate based on typical palletization
                    if (maxBoxes > 0) {
                        boxesPerPallet = Math.min(maxBoxes, 48); // Cap at reasonable number
                        boxesPerLayer = Math.min(Math.floor(Math.sqrt(boxesPerPallet)), 8);
                        console.log(`📦 Calculated boxes per pallet from dimensions: ${boxesPerPallet}`);
                    }
                }
            }

            // If still 0, it's a configuration error - log warning but don't use fallback
            if (boxesPerPallet === 0) {
                console.error(`❌ No boxesPerPallet configuration found for product ${item.code} (boxTypeId: ${boxTypeId})`);
                // We'll keep it as 0 - the UI will show this as an error state
            }

            // Store first values
            if (firstBoxesPerPallet === 0 && boxesPerPallet > 0) {
                firstBoxesPerPallet = boxesPerPallet;
                firstBoxesPerLayer = boxesPerLayer > 0 ? boxesPerLayer : 1;
            }

            // ============================================================
            // CALCULATE BOXES AND PALLETS
            // ============================================================

            // Calculate number of boxes
            let itemBoxes = 0;
            if (unitsPerBox > 0 && quantity > 0) {
                itemBoxes = quantity / unitsPerBox;
            } else if (quantity > 0) {
                itemBoxes = quantity;
            }

            // Calculate number of pallets using the formula: Paletes = quantidadeCaixas / caixasPorPalete
            let itemPallets = 0;
            if (boxesPerPallet > 0 && itemBoxes > 0) {
                itemPallets = itemBoxes / boxesPerPallet;
            }

            // Calculate weights
            let netWeightPerUnitInKg = productData?.netWeightInKg || 0;
            let grossWeightPerUnitInKg = productData?.grossWeightInKg || (netWeightPerUnitInKg * 1.1);
            const itemNetWeight = quantity * netWeightPerUnitInKg;
            const itemGrossWeight = quantity * grossWeightPerUnitInKg;

            // Accumulate totals
            totalCustomerUnits += quantity;
            totalBoxes += itemBoxes;
            totalNetWeight += itemNetWeight;
            totalGrossWeight += itemGrossWeight;
            totalPalletsFloat += itemPallets;

            // Build product description
            const ncm = productData?.ncm || '';
            const lotNumber = stockInfo?.lotNumber || '';
            const manufacturerDate = stockInfo?.manufacturerDateFormatted || '';
            const expirationDate = stockInfo?.expirationDateFormatted || '';
            const productName = item.name || item.code || '';
            const lotText = lotNumber ? `LOTE: ${lotNumber}` : 'LOTE: N/A';
            const manufacturerText = manufacturerDate ? `F:${manufacturerDate}` : 'F:__/____';
            const expirationText = expirationDate ? `V:${expirationDate}` : 'V:__/____';
            const ncmText = ncm ? `NCM: ${ncm}` : 'NCM: _______';
            const fullDescription = `${productName}\n${lotText}\n${manufacturerText}. ${expirationText}\n${ncmText}`;

            // Store product details
            itemDetails.push({
                code: item.code || '',
                name: fullDescription,
                originalName: productName,
                ncm: ncm,
                lotNumber: lotNumber,
                manufacturerDateFormatted: manufacturerDate,
                expirationDateFormatted: expirationDate,
                quantity: quantity,
                unitsPerBox: unitsPerBox,
                boxTypeName: boxTypeName,
                boxTypeId: boxTypeId,
                boxes: itemBoxes,
                boxesPerPallet: boxesPerPallet,
                pallets: itemPallets,
                netWeight: itemNetWeight,
                grossWeight: itemGrossWeight,
                boxDimensions: boxDimensions,
                palletDimensions: '',
                volumePerPallet: 0,
                totalVolume: 0,
                isBonus: item.isBonus || false,
                isSample: item.isSample || false,
                hasPalletConfig: boxesPerPallet > 0,
                palletConfigWarning: boxesPerPallet === 0 ? 'Sem configuração de palete' : null
            });

            // Store pallet config for later use
            if (matchingPallet && boxConfig) {
                productPalletConfigs.push({
                    productId: item.productId,
                    code: item.code,
                    pallet: matchingPallet,
                    config: boxConfig,
                    boxesPerPallet: boxesPerPallet
                });
            }
        }

        // ============================================================
        // CALCULATE PHYSICAL PALLETS
        // ============================================================

        const allPhysicalPallets = [];
        let totalBoxesRounded = Math.ceil(totalBoxes);
        let totalPhysicalPallets = Math.ceil(totalPalletsFloat);

        // Calculate effective boxes per pallet for the entire order
        const effectiveBoxesPerPallet = firstBoxesPerPallet > 0 ? firstBoxesPerPallet :
            (shippingMethod === 'air' ? 36 : 45); // Use reasonable default if no config found

        console.log(`📦 Total boxes: ${totalBoxesRounded}, Total pallets (sum): ${totalPalletsFloat}, Boxes per pallet: ${effectiveBoxesPerPallet}`);

        if (totalPhysicalPallets > 0) {
            const fullPallets = Math.floor(totalPalletsFloat);
            const hasPartial = totalPhysicalPallets > fullPallets;
            const partialFraction = totalPalletsFloat - fullPallets;
            const remainingBoxes = Math.ceil(effectiveBoxesPerPallet * partialFraction);

            const calculatePalletHeight = (boxH, boxesPerLayerVal, totalBoxesOnPallet) => {
                if (boxH <= 0 || boxesPerLayerVal <= 0 || totalBoxesOnPallet <= 0) return firstPalletBaseHeight || 15;
                const layers = Math.ceil(totalBoxesOnPallet / boxesPerLayerVal);
                return (firstPalletBaseHeight || 15) + (boxH * layers);
            };

            // Full pallets
            for (let i = 0; i < fullPallets; i++) {
                const fullHeight = calculatePalletHeight(firstBoxHeight, firstBoxesPerLayer || 1, effectiveBoxesPerPallet);
                const cappedHeight = Math.min(fullHeight, maxHeight);
                allPhysicalPallets.push({
                    width: firstPalletWidth || 100,
                    depth: firstPalletDepth || 120,
                    finalHeight: cappedHeight,
                    isPartial: false,
                    boxesOnPallet: effectiveBoxesPerPallet,
                    palletIndex: i + 1
                });
            }

            // Partial pallet
            if (hasPartial) {
                const partialHeight = calculatePalletHeight(firstBoxHeight, firstBoxesPerLayer || 1, remainingBoxes || 1);
                const cappedPartialHeight = Math.min(partialHeight, maxHeight);
                allPhysicalPallets.push({
                    width: firstPalletWidth || 100,
                    depth: firstPalletDepth || 120,
                    finalHeight: cappedPartialHeight,
                    isPartial: true,
                    boxesOnPallet: remainingBoxes || 1,
                    palletIndex: fullPallets + 1
                });
            }

            // Edge case handling
            if (fullPallets === 0 && !hasPartial && totalBoxesRounded > 0) {
                const partialHeight = calculatePalletHeight(firstBoxHeight, firstBoxesPerLayer || 1, totalBoxesRounded);
                const cappedPartialHeight = Math.min(partialHeight, maxHeight);
                allPhysicalPallets.push({
                    width: firstPalletWidth || 100,
                    depth: firstPalletDepth || 120,
                    finalHeight: cappedPartialHeight,
                    isPartial: true,
                    boxesOnPallet: totalBoxesRounded,
                    palletIndex: 1
                });
                totalPhysicalPallets = 1;
            }
        } else {
            // Fallback: single pallet with all boxes
            const defaultHeight = (firstPalletBaseHeight || 15) + (firstBoxHeight || 30);
            allPhysicalPallets.push({
                width: firstPalletWidth || 100,
                depth: firstPalletDepth || 120,
                finalHeight: Math.min(defaultHeight, maxHeight),
                isPartial: false,
                boxesOnPallet: totalBoxesRounded,
                palletIndex: 1
            });
            totalPhysicalPallets = 1;
        }

        // ============================================================
        // CALCULATE FINAL DIMENSIONS AND VOLUMES
        // ============================================================

        let finalPalletHeight = firstPalletBaseHeight || 15;
        if (allPhysicalPallets.length > 0) {
            const firstPallet = allPhysicalPallets[0];
            finalPalletHeight = firstPallet.finalHeight || finalPalletHeight;
        }
        const finalDimensions = `${firstPalletWidth || 100} x ${firstPalletDepth || 120} x ${Math.round(finalPalletHeight) || 15}`;

        let totalCombinedVolume = 0;
        allPhysicalPallets.forEach(pallet => {
            const volume = (pallet.width * pallet.depth * pallet.finalHeight) / 1000000;
            totalCombinedVolume += volume;
        });

        // Update item details with pallet info
        itemDetails.forEach(item => {
            const volumePerPallet = totalPhysicalPallets > 0 ? totalCombinedVolume / totalPhysicalPallets : 0;
            item.totalVolume = item.pallets * volumePerPallet;
            item.volumePerPallet = volumePerPallet;
            item.palletDimensions = finalDimensions;
        });

        // Log summary
        console.log(`✅ Packing calculation complete:`);
        console.log(`   Total boxes: ${totalBoxesRounded}`);
        console.log(`   Total pallets: ${totalPhysicalPallets}`);
        console.log(`   Boxes per pallet: ${effectiveBoxesPerPallet}`);
        console.log(`   Total volume: ${totalCombinedVolume} m³`);

        return {
            itemDetails,
            physicalPallets: allPhysicalPallets,
            totals: {
                customerUnits: totalCustomerUnits,
                totalBoxes: Math.ceil(totalBoxes),
                totalPallets: totalPhysicalPallets,
                netWeight: totalNetWeight,
                grossWeight: totalGrossWeight,
                totalVolume: totalCombinedVolume,
                dimensions: finalDimensions,
                shippingMethod: shippingMethod === 'air' ? 'AÉREO' : 'MARÍTIMO',
                boxesPerPalletUsed: effectiveBoxesPerPallet
            },
            hasData: true,
            selectedPalletTypeId: effectivePalletTypeId,
            client: {
                name: clientName,
                deliveryAddress: deliveryAddress,
                contacts: clientContacts,
                liveData: liveClientData
            }
        };
    } catch (error) {
        console.error('Error calculating packing data:', error);
        return null;
    }
};

// ============================================
// 4. MAIN COMPONENT
// ============================================

const PackingListPreview = ({
    order,
    onClose,
    onDownload,
    canGenerate = true,
    addToast,
    logoPath = '/assets/grupo_logo.png',
    itemsPerPage = 7,
}) => {
    const [loading, setLoading] = useState(true);
    const [packingData, setPackingData] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const printRef = useRef(null);

    // Split product items into pages
    const itemChunks = useMemo(() => {
        if (!packingData?.itemDetails || packingData.itemDetails.length === 0) return [];
        const chunks = [];
        for (let i = 0; i < packingData.itemDetails.length; i += itemsPerPage) {
            chunks.push(packingData.itemDetails.slice(i, i + itemsPerPage));
        }
        return chunks;
    }, [packingData?.itemDetails, itemsPerPage]);

    // Load packing data
    const loadPackingData = useCallback(async () => {
        setLoading(true);
        try {
            console.log('🔄 Loading packing data for order:', order?.code);
            const data = await calculatePackingData(order, order?.palletTypeId || null);

            if (data) {
                console.log('✅ Packing data loaded successfully');
                setPackingData(data);
            } else {
                console.warn('⚠️ No packing data returned');
                if (addToast) addToast('Não foi possível calcular os dados do packing list.', 'Warning');
            }
        } catch (error) {
            console.error('Error loading packing data:', error);
            if (addToast) addToast('Erro ao carregar dados do packing list.', 'Error');
        } finally {
            setLoading(false);
        }
    }, [order, addToast]);

    useEffect(() => {
        if (order) loadPackingData();
    }, [order, loadPackingData]);

    const handleDownload = async () => {
        if (!canGenerate) {
            if (addToast) addToast('Você não tem permissão para gerar este documento.', 'Warning');
            return;
        }
        setDownloading(true);
        try {
            if (onDownload) await onDownload();
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = () => {
        const printContent = printRef.current;
        if (!printContent) return;

        const printWindow = window.open('', '_blank', 'width=1200,height=800');
        if (!printWindow) {
            if (typeof addToast === 'function') {
                addToast('Por favor, permita pop-ups para imprimir.', 'Warning');
            } else {
                alert('Por favor, permita pop-ups para imprimir.');
            }
            return;
        }

        const clone = printContent.cloneNode(true);
        const noPrintElements = clone.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());
        clone.style.maxHeight = 'none';
        clone.style.height = 'auto';
        clone.style.overflow = 'visible';
        clone.style.background = '#ffffff';
        clone.style.boxShadow = 'none';
        clone.style.margin = '0';
        clone.style.padding = '0';

        const orderCode = order?.code || order?.id || 'Document';
        const clientName = packingData?.client?.name || order.clientName || 'N/A';

        // Check if any products are missing pallet configuration
        const missingConfig = packingData?.itemDetails?.filter(item => !item.hasPalletConfig) || [];
        const hasMissingConfig = missingConfig.length > 0;

        printWindow.document.write(`<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Packing List - ${orderCode}</title>
  <style>
    @page {
      size: A4 landscape;
      margin: 8mm 12mm 15mm 12mm;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      background: #ffffff !important;
      font-family: Calibri, Arial, sans-serif;
      font-size: 9.5px;
      color: #000000;
      width: 100%;
      height: 100%;
    }
    .print-document {
      width: 100%;
      margin: 0 auto;
      padding: 0 !important;
      background: #ffffff !important;
    }
    .page-break {
      page-break-after: always !important;
      break-after: page !important;
      display: flex !important;
      flex-direction: column !important;
      justify-content: space-between !important;
      min-height: 195mm !important;
      border-bottom: none !important;
      margin-bottom: 0 !important;
      padding-bottom: 0 !important;
    }
    .page-break:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
    .no-print { display: none !important; }
    .no-page-break {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .header-section {
      page-break-after: avoid !important;
      break-after: avoid !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .product-table-wrapper {
      overflow: visible !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .signatures-section {
      width: 100%;
      background: #ffffff !important;
      padding-top: 10px;
      padding-bottom: 5px;
      border-top: 1px solid #ccc;
      margin-top: auto !important;
      page-break-inside: avoid !important;
      break-inside: avoid !important;
    }
    .config-warning {
      background: #fff3cd !important;
      border: 1px solid #ffc107 !important;
      color: #856404 !important;
      padding: 6px 12px !important;
      border-radius: 4px !important;
      margin-bottom: 10px !important;
      font-size: 9px !important;
    }
  </style>
</head>
<body>
  <div class="print-document">
    ${clone.innerHTML}
  </div>
  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() {
        window.close();
      }, 500);
    };
  <\/script>
</body>
</html>`);
        printWindow.document.close();
    };

    if (!order) return null;

    const hasPackingData = packingData && packingData.hasData && packingData.itemDetails && packingData.itemDetails.length > 0;
    const physicalPallets = packingData?.physicalPallets || [];
    const dimensionGroups = groupPalletsByDimension(physicalPallets);
    const today = new Date();
    const arrivalDate = today.toLocaleDateString('pt-BR');

    // Check for missing configurations
    const missingConfig = packingData?.itemDetails?.filter(item => !item.hasPalletConfig) || [];
    const hasMissingConfig = missingConfig.length > 0;

    // Get client data from packing data or fallback to order
    const clientName = packingData?.client?.name || order.clientName || 'N/A';
    const clientAddress = packingData?.client?.deliveryAddress || order.deliveryAddress || order.destination || 'Endereço não informado';
    const clientEmail = packingData?.client?.contacts?.email || order.contactEmail || 'N/A';
    const clientPhone = packingData?.client?.contacts?.phone || order.contactPhone || 'N/A';

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div
                className={`${styles.formContainer} ${styles.formContainerLarge}`}
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: '1200px' }}
            >
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiFileText size={24} style={{ marginRight: '10px', color: '#1a237e' }} /> Packing List</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {clientName}
                            {packingData?.client?.liveData && (
                                <span style={{ marginLeft: '12px', background: '#4caf50', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', color: 'white' }}>
                                    DADOS ATUALIZADOS
                                </span>
                            )}
                            {packingData?.totals?.boxesPerPalletUsed && (
                                <span style={{ marginLeft: '12px', background: '#1976d2', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', color: 'white' }}>
                                    {packingData.totals.boxesPerPalletUsed} cx/palete
                                </span>
                            )}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={downloading} aria-label="Fechar">
                        <FiX size={24} />
                    </button>
                </div>

                <div
                    ref={printRef}
                    className={styles.formContent}
                    style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto', background: '#f5f5f5' }}
                >
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '8px' }}>
                            <FiRefreshCw size={40} className={styles.spinner} />
                            <p style={{ marginTop: '20px', color: '#666', fontSize: '16px' }}>Carregando dados do packing list...</p>
                        </div>
                    ) : !hasPackingData ? (
                        <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '8px', color: '#666' }}>
                            <FiAlertCircle size={40} style={{ color: '#ff9800' }} />
                            <p style={{ marginTop: '20px', fontSize: '16px' }}>Não foi possível calcular os dados do packing list.</p>
                        </div>
                    ) : (
                        <div style={{ background: '#fff', padding: '25px', borderRadius: '8px', fontFamily: 'Calibri, Arial, sans-serif' }}>
                            {/* Missing Configuration Warning */}
                            {hasMissingConfig && (
                                <div className="config-warning" style={{
                                    background: '#fff3cd',
                                    border: '1px solid #ffc107',
                                    color: '#856404',
                                    padding: '8px 16px',
                                    borderRadius: '4px',
                                    marginBottom: '15px',
                                    fontSize: '10px'
                                }}>
                                    <strong>⚠️ Atenção:</strong> Os seguintes produtos não possuem configuração de palete:
                                    {missingConfig.map(item => ` ${item.code} (${item.originalName})`).join(', ')}.
                                    Verifique o cadastro do tipo de caixa e palete.
                                </div>
                            )}

                            {itemChunks.map((pageItems, pageIdx) => {
                                const isLastPage = pageIdx === itemChunks.length - 1;
                                return (
                                    <div
                                        key={pageIdx}
                                        className="page-break"
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between',
                                            minHeight: '560px',
                                            marginBottom: isLastPage ? '0' : '40px',
                                            paddingBottom: isLastPage ? '0' : '20px',
                                            borderBottom: isLastPage ? 'none' : '2px dashed #bbb'
                                        }}
                                    >
                                        <div style={{ flex: '1 0 auto' }}>
                                            {/* HEADER */}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                borderBottom: '2px solid #1a237e',
                                                paddingBottom: '12px',
                                                marginBottom: '12px'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                    <div style={{
                                                        width: '200px',
                                                        height: '70px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ccc',
                                                        overflow: 'hidden',
                                                        background: '#fff',
                                                        padding: '6px'
                                                    }}>
                                                        <img
                                                            src={logoPath}
                                                            alt="Logo"
                                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = '<span style="font-size: 12px; color: #666; font-weight: bold;">LOGO</span>';
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'center' }}>
                                                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a237e', letterSpacing: '2px' }}>
                                                        PACKING LIST
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#333', marginTop: '4px' }}>
                                                        Ref: {order.code}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#333' }}>
                                                        DATE: {arrivalDate}
                                                    </div>
                                                </div>
                                                <div style={{ textAlign: 'left' }}>
                                                    <div style={{ fontSize: '9px', color: '#666' }}>PAG. {pageIdx + 1}/{itemChunks.length}</div>
                                                    <div style={{ fontSize: '9px', color: '#666' }}>FQ-37</div>
                                                    <div style={{ fontSize: '9px', color: '#666' }}>REVISION: 01</div>
                                                </div>
                                            </div>

                                            {pageIdx === 0 && (
                                                <div className="header-section" style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr 1fr',
                                                    gap: '15px',
                                                    marginBottom: '12px',
                                                    border: '1px solid #ddd',
                                                    padding: '12px',
                                                    background: '#fafafa'
                                                }}>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '8px', color: '#666' }}>SHIPPER - EXPORTER:</div>
                                                        <div style={{ fontSize: '8px', fontWeight: 'bold' }}>GRUPO B2E IND. E COM. DE COSMETICOS LTDA</div>
                                                        <div style={{ fontSize: '8px' }}>AVENIDA JOAO PAULO ABLAS NO. 1400, GALPAO NO. 4 COTIA,</div>
                                                        <div style={{ fontSize: '8px' }}>SP, CEP: 06711-250, BRAZIL</div>
                                                        <div style={{ fontSize: '8px' }}>PHONE: +55(11) 3637-7711</div>
                                                        <div style={{ fontSize: '8px' }}>EMAIL: export@brazil2export.com</div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '8px', color: '#666' }}>CLIENT:</div>
                                                        <div style={{ fontSize: '8px', fontWeight: 'bold' }}>{clientName}</div>
                                                        <div style={{ fontSize: '8px' }}>{clientAddress}</div>
                                                        <div style={{ fontSize: '8px' }}>PHONE: {clientPhone}</div>
                                                        <div style={{ fontSize: '8px' }}>EMAIL: {clientEmail}</div>
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold', fontSize: '9px', color: '#666' }}>SHIP TO:</div>
                                                        <div style={{ fontSize: '8px', fontWeight: 'bold' }}>{clientName}</div>
                                                        <div style={{ fontSize: '8px' }}>{clientAddress}</div>
                                                        <div style={{ fontSize: '8px' }}>PHONE: {clientPhone}</div>
                                                        <div style={{ fontSize: '8px' }}>EMAIL: {clientEmail}</div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* PRODUCT TABLE */}
                                            <div className="product-table-wrapper" style={{ marginBottom: '12px', overflow: 'visible' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', border: '1px solid #ddd' }}>
                                                    <thead>
                                                        <tr style={{ background: '#e8eaf6' }}>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '40px' }}>ITEMS</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left', fontWeight: 'bold' }}>PRODUCT</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '65px' }}>CUSTOMER ORDER<br />(IN UNITS)</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '50px' }}>UNITS<br />PER BOX</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '60px' }}>TOTAL NO.<br />OF BOXES</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '60px' }}>BOXES PER<br />PALLET</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '55px' }}>NO. OF<br />PALLET</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '65px' }}>TOTAL NET<br />WEIGHT(KGS)</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '65px' }}>TOTAL GROSS<br />WEIGHT(KGS)</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '70px' }}>PALLET<br />DIMENSIONS</th>
                                                            <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontWeight: 'bold', width: '65px' }}>VOLUME</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pageItems.map((item, itemIdx) => {
                                                            const globalIdx = pageIdx * itemsPerPage + itemIdx;
                                                            const isFirstItemInPage = itemIdx === 0;
                                                            const totalPallets = packingData.totals.totalPallets || 0;
                                                            const palletDim = packingData.totals.dimensions || '';
                                                            const totalVolume = packingData.totals.totalVolume || 0;
                                                            const isMissingConfig = !item.hasPalletConfig;

                                                            return (
                                                                <tr key={itemIdx} style={isMissingConfig ? { background: '#fff3cd' } : (item.isBonus || item.isSample ? { background: '#fff3e0' } : {})}>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{globalIdx + 1}</td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', whiteSpace: 'pre-wrap', fontSize: '9px', lineHeight: '1.3' }}>
                                                                        <div>{item.name}</div>
                                                                        <div style={{ fontSize: '9px', color: '#666' }}>
                                                                            {item.isBonus && <span style={{ background: '#ff9800', color: '#fff', padding: '1px 4px', borderRadius: '2px', marginRight: '4px' }}>BÔNUS</span>}
                                                                            {item.isSample && <span style={{ background: '#2196f3', color: '#fff', padding: '1px 4px', borderRadius: '2px', marginRight: '4px' }}>AMOSTRA</span>}
                                                                            {isMissingConfig && <span style={{ background: '#ffc107', color: '#000', padding: '1px 4px', borderRadius: '2px', marginLeft: '4px' }}>⚠️ SEM CONFIG</span>}
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(item.quantity, 0)}</td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(item.unitsPerBox, 0)}</td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(item.boxes, 2)}</td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                                        {item.boxesPerPallet > 0 ? formatNumber(item.boxesPerPallet, 0) : '-'}
                                                                    </td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>
                                                                        {item.pallets > 0 ? formatNumber(item.pallets, 2) : '-'}
                                                                    </td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatWeight(item.netWeight)}</td>
                                                                    <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatWeight(item.grossWeight)}</td>
                                                                    {isFirstItemInPage ? (
                                                                        <td
                                                                            rowSpan={pageItems.length}
                                                                            style={{
                                                                                border: '1px solid #ddd',
                                                                                padding: '6px',
                                                                                textAlign: 'center',
                                                                                verticalAlign: 'middle',
                                                                                backgroundColor: '#f8f9fa'
                                                                            }}
                                                                        >
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center' }}>
                                                                                {dimensionGroups.length > 0 ? (
                                                                                    dimensionGroups.map((group, gIdx) => (
                                                                                        <div key={gIdx} style={{ fontSize: '10px', display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                                                                                            <span style={{
                                                                                                background: '#e2e8f0',
                                                                                                padding: '1px 6px',
                                                                                                borderRadius: '3px',
                                                                                                fontWeight: 'bold',
                                                                                                fontSize: '9px',
                                                                                                minWidth: '25px',
                                                                                                textAlign: 'center',
                                                                                                color: '#334155'
                                                                                            }}>
                                                                                                {group.count}x
                                                                                            </span>
                                                                                            {group.dimensions}
                                                                                        </div>
                                                                                    ))
                                                                                ) : (
                                                                                    <div style={{ fontSize: '10px' }}>
                                                                                        {totalPallets > 0 ? `${totalPallets}x ${palletDim}` : palletDim || '-'}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </td>
                                                                    ) : null}
                                                                    {isFirstItemInPage ? (
                                                                        <td
                                                                            rowSpan={pageItems.length}
                                                                            style={{
                                                                                border: '1px solid #ddd',
                                                                                padding: '6px',
                                                                                textAlign: 'center',
                                                                                verticalAlign: 'middle',
                                                                                fontWeight: 'bold',
                                                                                backgroundColor: '#f8f9fa'
                                                                            }}
                                                                        >
                                                                            <div>{formatVolume(totalVolume)} m³</div>
                                                                        </td>
                                                                    ) : null}
                                                                </tr>
                                                            );
                                                        })}
                                                        {/* TOTAL ROW */}
                                                        {isLastPage && (
                                                            <tr style={{ fontWeight: 'bold', background: '#e8eaf6' }}>
                                                                <td colSpan="2" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>TOTAL</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(packingData.totals.customerUnits, 0)}</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(packingData.totals.totalBoxes, 0)}</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatNumber(packingData.totals.totalPallets, 0)}</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatWeight(packingData.totals.netWeight)}</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}>{formatWeight(packingData.totals.grossWeight)}</td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center', fontSize: '10px' }}></td>
                                                                <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'center' }}></td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        {/* SIGNATURES */}
                                        <div className="signatures-section" style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr 1fr',
                                            gap: '20px',
                                            marginTop: 'auto',
                                            paddingTop: '10px',
                                            paddingBottom: '5px',
                                            borderTop: '1px solid #ccc'
                                        }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '8px' }}>Elaborado por: Nazir Farra</div>
                                                <div style={{ fontSize: '9px', color: '#666', marginTop: '1px', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
                                                    Data: 24 / 08 / 2026
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '8px' }}>Revisado por: Magali Murro</div>
                                                <div style={{ fontSize: '9px', color: '#666', marginTop: '1px', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
                                                    Data: 24 / 08 / 2026
                                                </div>
                                            </div>
                                            <div style={{ textAlign: 'center' }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '8px' }}>Aprovado por: Nisrine El Hazzat</div>
                                                <div style={{ fontSize: '9px', color: '#666', marginTop: '1px', borderTop: '1px solid #ccc', paddingTop: '4px' }}>
                                                    Data: 24 / 08 / 2026
                                                </div>
                                            </div>
                                        </div>

                                        {/* AUTOMATIC GENERATION NOTE */}
                                        <div style={{
                                            textAlign: 'right',
                                            fontSize: '8px',
                                            color: '#666',
                                            marginTop: '6px',
                                            fontStyle: 'italic'
                                        }}>
                                            Documento gerado eletronicamente em: {formatCurrentDateTime()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={downloading}>
                        Fechar
                    </button>
                    <button
                        type="button"
                        onClick={handlePrint}
                        className={styles.printButton}
                        disabled={loading || !hasPackingData}
                        title="Imprimir Packing List"
                        style={{
                            padding: '8px 20px',
                            borderRadius: '4px',
                            border: 'none',
                            background: '#1a237e',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            fontSize: '14px'
                        }}
                    >
                        <FiPrinter size={18} />
                        Imprimir
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PackingListPreview;